import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  RefreshCcw, Sparkles, Landmark, Ban, Save, TrendingUp, Users, MessageCircle,
  Send, Shield, DollarSign, CheckCircle2, Clock, XCircle, Image as ImageIcon,
  ChevronRight, Upload, Link2, Share2, Copy, Plus, Trash2, UserPlus, MessageSquare,
  Gift, Rocket, Trophy, ClipboardList,
} from "lucide-react";
import WorkQueueBoard from "@/components/app/WorkQueueBoard";

const TIERS = [150, 250, 350, 500];

const STATUSES = [
  "Payment Setup Pending",
  "Awaiting Info",
  "Approved",
  "Posted",
  "Completed",
  "Refunded",
  "Cancelled",
];

const STATUS_TONES = {
  "Payment Setup Pending": "bg-amber-50 text-amber-800 border-amber-200",
  "Awaiting Info":         "bg-sky-50 text-sky-800 border-sky-200",
  "Approved":              "bg-indigo-50 text-indigo-800 border-indigo-200",
  "Posted":                "bg-violet-50 text-violet-800 border-violet-200",
  "Completed":             "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Refunded":              "bg-slate-100 text-slate-700 border-slate-200",
  "Cancelled":             "bg-rose-50 text-rose-700 border-rose-200",
};

export default function TradelineAdminDashboard() {
  const [tab, setTab] = useState("queue"); // queue | inventory | referrals | team
  const [reservations, setReservations] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [distribution, setDistribution] = useState({});
  const [lastSync, setLastSync] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const loadAll = async () => {
    try {
      const [{ data: resData }, { data: invData }] = await Promise.all([
        api.get("/admin/tradelines/reservations"),
        api.get("/admin/tradelines"),
      ]);
      setReservations(resData?.items || []);
      setInventory(invData?.items || []);
      setDistribution(invData?.distribution || {});
      setLastSync(invData?.last_synced_at || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  // Deep-link support: /tradeline-admin?reservation=<id>&tab=<name>
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const rid = p.get("reservation");
    const t = p.get("tab");
    if (t) setTab(t);
    if (rid) { setTab("queue"); setActiveId(rid); }
  }, []);

  const stats = useMemo(() => {
    const s = { pending: 0, awaiting: 0, posted: 0, completed: 0, revenue: 0 };
    for (const r of reservations) {
      if (r.status === "Payment Setup Pending") s.pending++;
      if (r.status === "Awaiting Info") s.awaiting++;
      if (r.status === "Posted") s.posted++;
      if (r.status === "Completed") s.completed++;
      if (["Approved", "Posted", "Completed"].includes(r.status)) s.revenue += Number(r.total_price || 0);
    }
    return s;
  }, [reservations]);

  const forceSync = async () => {
    setBusy(true);
    try {
      await api.post("/admin/tradelines/sync");
      toast.success("Synced with supplier");
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sync failed");
    } finally { setBusy(false); }
  };

  const reprice = async (id) => {
    try {
      const { data } = await api.post(`/admin/tradelines/${id}/reprice`);
      setInventory((prev) => prev.map((x) => x.id === id ? { ...x, ...data } : x));
      toast.success("Repriced by AI");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reprice failed");
    }
  };

  const overrideInv = async (id, updates) => {
    try {
      const { data } = await api.put(`/admin/tradelines/${id}`, updates);
      setInventory((prev) => prev.map((x) => x.id === id ? { ...x, ...data } : x));
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const active = reservations.find((r) => r.id === activeId);

  return (
    <AppShell>
      <div className="cq-in max-w-6xl">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] font-bold text-amber-600">
          <Shield size={12} /> Tradeline Ops
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">Reservation Command Center</h1>
        <p className="text-slate-600 mt-1">Every tradeline order lands here. Update status, capture the AU info, upload proof of posting, and chat with the client in‑portal — no data leaks outside your queue.</p>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard testid="stat-pending"   icon={<Clock size={16} />}       label="Payment Pending" value={stats.pending} tone="amber" />
          <StatCard testid="stat-awaiting"  icon={<Users size={16} />}       label="Awaiting Info"   value={stats.awaiting} tone="sky" />
          <StatCard testid="stat-posted"    icon={<TrendingUp size={16} />}  label="Posted"          value={stats.posted} tone="violet" />
          <StatCard testid="stat-completed" icon={<CheckCircle2 size={16} />} label="Completed"       value={stats.completed} tone="emerald" />
          <StatCard testid="stat-revenue"   icon={<DollarSign size={16} />}  label="Live Revenue"    value={`$${stats.revenue.toLocaleString()}`} tone="slate" />
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b border-slate-200 flex gap-1 flex-wrap">
          <TabBtn active={tab === "queue"} onClick={() => setTab("queue")} testid="tab-queue">
            <Users size={14} className="mr-1" /> Reservations ({reservations.length})
          </TabBtn>
          <TabBtn active={tab === "nextup"} onClick={() => setTab("nextup")} testid="tab-nextup">
            <ClipboardList size={14} className="mr-1" /> Next Up
          </TabBtn>
          <TabBtn active={tab === "inventory"} onClick={() => setTab("inventory")} testid="tab-inventory">
            <Landmark size={14} className="mr-1" /> Inventory ({inventory.length})
          </TabBtn>
          <TabBtn active={tab === "referrals"} onClick={() => setTab("referrals")} testid="tab-referrals">
            <Gift size={14} className="mr-1" /> Referrals
          </TabBtn>
          <TabBtn active={tab === "team"} onClick={() => setTab("team")} testid="tab-team">
            <MessageSquare size={14} className="mr-1" /> Team Chat
          </TabBtn>
        </div>

        {tab === "nextup" && (
          <div className="mt-6">
            <p className="text-sm text-slate-500 mb-3">
              Everyone Irving and the team have queued up for you to post. Drag between columns as you work — the whole team sees your progress live.
            </p>
            <WorkQueueBoard kind="tradeline" emptyHint="No orders queued yet — Irving will add paid reservations here." />
          </div>
        )}

        {tab === "queue" && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr,1.2fr] gap-4">
            {/* List */}
            <Card className="rounded-2xl border-slate-100 divide-y max-h-[70vh] overflow-y-auto" data-testid="tl-res-list">
              {reservations.length === 0 && (
                <div className="p-8 text-center text-sm text-slate-400">No reservations yet — new ones will land here the moment a client checks out.</div>
              )}
              {reservations.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setActiveId(r.id)}
                  className={`w-full text-left p-4 hover:bg-slate-50 transition ${activeId === r.id ? "bg-amber-50" : ""}`}
                  data-testid={`res-row-${r.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-widest font-black rounded-full border px-2 py-0.5 ${STATUS_TONES[r.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{r.status}</span>
                    <p className="font-display font-black text-slate-900 flex-1 truncate">{r.client_name}</p>
                    <p className="font-display font-black text-amber-600">${r.total_price}</p>
                    <ChevronRight size={14} className="text-slate-400" />
                  </div>
                  <p className="text-xs text-slate-500 mt-1 truncate">
                    {(r.line_items || []).map((i) => `${i.bank} $${(i.credit_limit||0).toLocaleString()}`).join(" · ")}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{new Date(r.created_at).toLocaleString()}</p>
                </button>
              ))}
            </Card>
            {/* Detail */}
            {active ? (
              <ReservationDetail
                key={active.id}
                reservation={active}
                onChanged={(next) => setReservations((prev) => prev.map((x) => x.id === next.id ? { ...x, ...next } : x))}
              />
            ) : (
              <Card className="rounded-2xl border-dashed border-slate-200 p-8 flex flex-col items-center justify-center text-center text-sm text-slate-400" data-testid="res-empty-detail">
                <MessageCircle size={22} className="mb-2 text-slate-300" />
                Pick a reservation on the left to view the client info, update status, upload proof, and chat.
              </Card>
            )}
          </div>
        )}

        {tab === "inventory" && (
          <div className="mt-6">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Button onClick={forceSync} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="tl-force-sync">
                <RefreshCcw size={14} className="mr-1" /> Force sync
              </Button>
              <Button onClick={() => setShowAddForm((v) => !v)} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="tl-add-toggle">
                <Plus size={14} className="mr-1" /> {showAddForm ? "Close form" : "Add my tradeline"}
              </Button>
              <span className="text-xs text-slate-500">Last sync: <b>{lastSync ? new Date(lastSync).toLocaleString() : "never"}</b></span>
              <span className="text-xs text-slate-500 inline-flex items-center gap-2">
                Distribution:
                {TIERS.map((t) => (
                  <span key={t} className="inline-flex items-center gap-0.5">
                    <b className="text-slate-800">{distribution[String(t)] || 0}</b>
                    <span className="text-slate-400">×${t}</span>
                  </span>
                ))}
              </span>
            </div>

            {showAddForm && (
              <AddTradelineForm
                onCreated={(t) => { setInventory((prev) => [t, ...prev]); setShowAddForm(false); toast.success("Tradeline added"); }}
              />
            )}

            <Card className="rounded-2xl border-slate-100 divide-y">
              {inventory.map((t) => {
                const margin = (t.retail_price - t.wholesale_cost).toFixed(2);
                const isSelf = t.supplier === "self";
                return (
                  <div key={t.id} className={`p-4 flex flex-wrap items-start gap-3 ${!t.is_active ? "opacity-50" : ""}`} data-testid={`tl-inv-${t.id}`}>
                    <div className={`h-10 w-10 rounded-xl text-white flex items-center justify-center ${isSelf ? "bg-amber-500" : "bg-slate-900"}`}>
                      <Landmark size={16} />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <p className="font-display font-black text-slate-900 flex items-center gap-2 flex-wrap">
                        {t.bank} · ${t.credit_limit?.toLocaleString?.() || t.credit_limit}
                        {isSelf && (
                          <span className="text-[10px] uppercase tracking-widest font-black rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5">
                            Mine · {t.owner_name}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500">
                        {t.account_age_years}yr · {t.utilization_pct}% util · {t.reporting_period} · {t.spots_available} spots · {t.supplier || "supplier"}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1"><b className="text-slate-700">AI:</b> {t.ai_reasoning || "—"}</p>
                    </div>
                    <div className="text-xs text-slate-500">
                      <p>Wholesale: <b className="text-slate-800">${Number(t.wholesale_cost).toFixed(2)}</b></p>
                      <p>Retail:   <b className={margin < 0 ? "text-rose-600" : "text-emerald-700"}>${t.retail_price}</b></p>
                      <p>Margin:  <b className={margin < 0 ? "text-rose-600" : "text-slate-800"}>${margin}</b></p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={t.retail_price}
                        onChange={(e) => overrideInv(t.id, { retail_price: Number(e.target.value), notes: "Manual override from Tradeline Ops" })}
                        className="h-9 rounded-md border border-slate-200 px-2 text-sm"
                        data-testid={`tl-price-${t.id}`}
                      >
                        {TIERS.map((tier) => <option key={tier} value={tier}>${tier}</option>)}
                      </select>
                      <Button variant="outline" onClick={() => reprice(t.id)} data-testid={`tl-reprice-${t.id}`}>
                        <Sparkles size={13} className="mr-1" /> AI reprice
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => overrideInv(t.id, { is_active: !t.is_active })}
                        className={t.is_active ? "" : "border-rose-200 text-rose-600"}
                        data-testid={`tl-active-${t.id}`}
                      >
                        <Ban size={13} className="mr-1" /> {t.is_active ? "Disable" : "Enable"}
                      </Button>
                      {isSelf && (
                        <Button
                          variant="outline"
                          onClick={async () => {
                            if (!window.confirm(`Delete ${t.bank} $${t.credit_limit}?`)) return;
                            try { await api.delete(`/admin/tradelines/${t.id}`); setInventory((prev) => prev.filter((x) => x.id !== t.id)); toast.success("Deleted"); }
                            catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
                          }}
                          className="border-rose-200 text-rose-600"
                          data-testid={`tl-delete-${t.id}`}
                        >
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {inventory.length === 0 && (
                <div className="p-8 text-center text-sm text-slate-400">
                  No inventory yet — hit <b>Force sync</b> or <b>Add my tradeline</b> above.
                </div>
              )}
            </Card>
          </div>
        )}

        {tab === "referrals" && <ReferralsTab />}
        {tab === "team" && <TeamChatTab />}
      </div>
    </AppShell>
  );
}

function StatCard({ icon, label, value, tone = "amber", testid }) {
  const tones = {
    amber:   "bg-amber-50 text-amber-700 border-amber-100",
    sky:     "bg-sky-50 text-sky-700 border-sky-100",
    violet:  "bg-violet-50 text-violet-700 border-violet-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    slate:   "bg-slate-50 text-slate-700 border-slate-100",
  };
  return (
    <Card className={`p-3 rounded-2xl border ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold">{icon}{label}</div>
      <p className="font-display font-black text-2xl mt-1 text-slate-900">{value}</p>
    </Card>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center px-4 py-2 text-sm font-bold border-b-2 -mb-px transition ${
        active ? "border-amber-500 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

function ReservationDetail({ reservation, onChanged }) {
  const [status, setStatus] = useState(reservation.status);
  const [ssn, setSsn] = useState(reservation.client_ssn || "");
  const [dob, setDob] = useState(reservation.client_dob || "");
  const [addr, setAddr] = useState(reservation.client_address || "");
  const [notes, setNotes] = useState(reservation.admin_notes || "");
  const [proofUrl, setProofUrl] = useState(reservation.proof_url || "");
  const [proofImg, setProofImg] = useState(reservation.proof_image_base64 || "");
  const [saving, setSaving] = useState(false);

  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [msgBusy, setMsgBusy] = useState(false);

  const loadMessages = async () => {
    try {
      const { data } = await api.get(`/admin/tradelines/reservations/${reservation.id}/messages`);
      setMessages(data?.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadMessages(); /* eslint-disable-next-line */ }, [reservation.id]);
  useEffect(() => {
    const t = setInterval(loadMessages, 8000);
    return () => clearInterval(t);
  // eslint-disable-next-line
  }, [reservation.id]);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = {
        status,
        client_ssn: ssn,
        client_dob: dob,
        client_address: addr,
        admin_notes: notes,
        proof_url: proofUrl,
        ...extra,
      };
      const { data } = await api.put(`/admin/tradelines/reservations/${reservation.id}`, payload);
      onChanged(data);
      toast.success("Reservation saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const refund = async () => {
    if (!window.confirm("Mark this reservation as REFUNDED? The client will be notified.")) return;
    try {
      const { data } = await api.post(`/admin/tradelines/reservations/${reservation.id}/refund`);
      onChanged(data);
      setStatus("Refunded");
      toast.success("Reservation refunded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Refund failed");
    }
  };

  const uploadProof = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result || "");
      setProofImg(b64);
      await save({ proof_image_base64: b64 });
    };
    reader.readAsDataURL(file);
  };

  const sendMsg = async () => {
    const t = msgText.trim();
    if (!t) return;
    setMsgBusy(true);
    try {
      const { data } = await api.post(`/admin/tradelines/reservations/${reservation.id}/messages`, { text: t });
      setMessages((prev) => [...prev, data]);
      setMsgText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setMsgBusy(false); }
  };

  return (
    <Card className="rounded-2xl border-slate-100 p-4 flex flex-col gap-4 max-h-[70vh] overflow-y-auto" data-testid="res-detail">
      <div>
        <p className="text-[10px] uppercase tracking-widest font-black text-amber-600">Reservation · {reservation.id.slice(0, 8)}</p>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          <h3 className="font-display font-black text-2xl text-slate-900">{reservation.client_name}</h3>
          <span className={`text-[10px] uppercase tracking-widest font-black rounded-full border px-2 py-0.5 ${STATUS_TONES[reservation.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
            {reservation.status}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          <a href={`mailto:${reservation.client_email}`} className="underline">{reservation.client_email}</a>
          {reservation.client_phone ? <> · <a href={`tel:${reservation.client_phone}`} className="underline">{reservation.client_phone}</a></> : null}
        </p>
      </div>

      {/* Line items */}
      <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
        <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">Order</p>
        <div className="space-y-1">
          {(reservation.line_items || []).map((i) => (
            <div key={i.id} className="flex items-center gap-2 text-sm">
              <Landmark size={14} className="text-slate-400" />
              <p className="flex-1"><b>{i.bank}</b> · ${Number(i.credit_limit || 0).toLocaleString()} · {i.account_age_years}yr</p>
              <p className="font-bold text-amber-600">${i.adjusted_price ?? i.retail_price}</p>
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between text-sm">
          <p className="text-slate-500">
            Subtotal ${reservation.subtotal_price}
            {reservation.discount_amount ? <> · <span className="text-emerald-700">−${reservation.discount_amount}</span></> : null}
            {reservation.promo_code ? <> · <span className="font-bold">{reservation.promo_code}</span></> : null}
          </p>
          <p className="font-display font-black text-lg text-slate-900">${reservation.total_price}</p>
        </div>
      </div>

      {/* Status control */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto] gap-2 items-end">
        <label className="text-xs font-bold text-slate-700">
          Order status
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid="res-status-select">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <div className="flex gap-2">
          <Button onClick={() => save()} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="res-save-btn">
            <Save size={14} className="mr-1" /> Save
          </Button>
          <Button onClick={refund} variant="outline" className="border-rose-200 text-rose-600" data-testid="res-refund-btn">
            <XCircle size={14} className="mr-1" /> Refund
          </Button>
        </div>
      </div>

      {/* Sensitive AU info */}
      <details className="rounded-xl border border-amber-200 bg-amber-50/40 p-3" data-testid="res-au-info">
        <summary className="cursor-pointer font-bold text-sm text-amber-800 flex items-center gap-2">
          <Shield size={14} /> Authorized-User info (sensitive)
        </summary>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="text-xs font-bold text-slate-700">
            SSN
            <Input value={ssn} onChange={(e) => setSsn(e.target.value)} placeholder="XXX-XX-XXXX" data-testid="res-ssn" />
          </label>
          <label className="text-xs font-bold text-slate-700">
            DOB
            <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} data-testid="res-dob" />
          </label>
          <label className="text-xs font-bold text-slate-700 sm:col-span-2">
            Mailing address (for card delivery)
            <Input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Street, City, State, ZIP" data-testid="res-addr" />
          </label>
        </div>
      </details>

      {/* Proof of posting */}
      <div className="rounded-xl border border-slate-200 p-3">
        <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1"><ImageIcon size={12}/> Proof of posting</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={proofUrl} onChange={(e) => setProofUrl(e.target.value)} placeholder="Paste screenshot URL…" data-testid="res-proof-url" />
          <label className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 text-sm font-bold text-slate-700 cursor-pointer hover:bg-slate-50">
            <Upload size={14} /> Upload
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadProof(e.target.files?.[0])} data-testid="res-proof-file" />
          </label>
        </div>
        {(proofImg || proofUrl) && (
          <div className="mt-3">
            <img src={proofImg || proofUrl} alt="Proof of posting" className="max-h-52 rounded-lg border border-slate-200" data-testid="res-proof-preview" />
          </div>
        )}
      </div>

      {/* Internal notes */}
      <label className="text-xs font-bold text-slate-700">
        Internal notes (only visible to Tradeline Ops)
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Client rushed for post before Nov 5th…" data-testid="res-notes" />
      </label>

      {/* Chat */}
      <div className="rounded-xl border border-slate-200 p-3 flex flex-col gap-2" data-testid="res-chat">
        <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1"><MessageCircle size={12}/> Portal chat with {reservation.client_name}</p>
        <div className="max-h-56 min-h-[80px] overflow-y-auto flex flex-col gap-2 bg-slate-50 rounded-lg p-2">
          {messages.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No messages yet — say hi.</p>}
          {messages.map((m) => {
            const mine = m.sender_role !== "client";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${mine ? "bg-amber-500 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                  <p className={`text-[10px] font-bold mb-0.5 ${mine ? "text-white/80" : "text-slate-500"}`}>{m.sender_name}</p>
                  <p className="whitespace-pre-wrap">{m.text}</p>
                  <p className={`text-[9px] mt-0.5 ${mine ? "text-white/70" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2">
          <Input value={msgText} onChange={(e) => setMsgText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendMsg(); }} placeholder="Type a message to the client…" data-testid="msg-input" />
          <Button onClick={sendMsg} disabled={msgBusy || !msgText.trim()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="msg-send">
            <Send size={14} className="mr-1" /> Send
          </Button>
        </div>
        <p className="text-[10px] text-slate-400">Messages here appear in the client's tradeline order page, isolated from their credit-repair thread.</p>
      </div>
    </Card>
  );
}

// ============================================================
// Add Tradeline (Mark's own AU seat)
// ============================================================
function AddTradelineForm({ onCreated }) {
  const [form, setForm] = useState({
    bank: "", credit_limit: "", account_age_years: "", utilization_pct: 3,
    reporting_period: "Monthly", spots_available: 3, wholesale_cost: "",
    opened_year: "", retail_price: 250, notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bank || !form.credit_limit || !form.account_age_years || !form.wholesale_cost) {
      toast.error("Bank, credit limit, age, and wholesale cost are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        credit_limit: Number(form.credit_limit),
        account_age_years: Number(form.account_age_years),
        utilization_pct: Number(form.utilization_pct),
        spots_available: Number(form.spots_available),
        wholesale_cost: Number(form.wholesale_cost),
        opened_year: form.opened_year ? Number(form.opened_year) : null,
        retail_price: Number(form.retail_price),
      };
      const { data } = await api.post("/admin/tradelines", payload);
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add");
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Card className="p-4 rounded-2xl border-amber-200 bg-amber-50/40 mb-4" data-testid="tl-add-form">
      <p className="text-[11px] uppercase tracking-widest font-black text-amber-700 flex items-center gap-1"><Plus size={12} /> Add your own tradeline</p>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">Rebate goes to your MARKE code. We handle payments + posting proof end-to-end.</p>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><Label>Bank / Issuer</Label><Input value={form.bank} onChange={set("bank")} placeholder="e.g. Amex Gold" data-testid="add-bank" required /></div>
        <div><Label>Credit limit ($)</Label><Input type="number" value={form.credit_limit} onChange={set("credit_limit")} placeholder="25000" data-testid="add-limit" required /></div>
        <div><Label>Account age (years)</Label><Input type="number" step="0.5" value={form.account_age_years} onChange={set("account_age_years")} placeholder="8" data-testid="add-age" required /></div>
        <div><Label>Utilization %</Label><Input type="number" value={form.utilization_pct} onChange={set("utilization_pct")} data-testid="add-util" /></div>
        <div>
          <Label>Reporting</Label>
          <select value={form.reporting_period} onChange={set("reporting_period")} className="h-10 w-full rounded-md border border-slate-200 px-2 text-sm" data-testid="add-report">
            <option>Monthly</option><option>Bi-Monthly</option><option>Quarterly</option>
          </select>
        </div>
        <div><Label>Spots available</Label><Input type="number" value={form.spots_available} onChange={set("spots_available")} data-testid="add-spots" /></div>
        <div><Label>Wholesale cost ($)</Label><Input type="number" step="0.01" value={form.wholesale_cost} onChange={set("wholesale_cost")} placeholder="90" data-testid="add-wholesale" required /></div>
        <div><Label>Opened year</Label><Input type="number" value={form.opened_year} onChange={set("opened_year")} placeholder="2016" data-testid="add-year" /></div>
        <div>
          <Label>Retail tier</Label>
          <select value={form.retail_price} onChange={set("retail_price")} className="h-10 w-full rounded-md border border-slate-200 px-2 text-sm" data-testid="add-retail">
            {[150, 250, 350, 500].map((p) => <option key={p} value={p}>${p}</option>)}
          </select>
        </div>
        <div className="sm:col-span-3"><Label>Notes (internal)</Label><Textarea rows={2} value={form.notes} onChange={set("notes")} placeholder="Anything the team should know…" data-testid="add-notes" /></div>
        <div className="sm:col-span-3 flex justify-end">
          <Button type="submit" disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-submit">
            {busy ? "Adding…" : "Add tradeline"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ============================================================
// Referrals tab
// ============================================================
function ReferralsTab() {
  const [codes, setCodes] = useState([]);
  const [refs, setRefs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: c }, { data: r }] = await Promise.all([
        api.get("/partner/referral-codes"),
        api.get("/partner/referrals"),
      ]);
      setCodes(c?.items || []);
      setRefs(r?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  // Always share the clean production domain (short /r/CODE URL). The
  // /r/:code frontend route tracks the click and forwards to /register.
  const SHARE_HOST = "clientquack.com";
  const buildUrl = (c) => `https://${SHARE_HOST}/r/${c}`;

  const copyLink = (code) => {
    const url = buildUrl(code);
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  };

  const share = async (code) => {
    const url = buildUrl(code);
    const text = `Get your credit tracked with Credit Quack — signup takes 30 seconds. Use my link:`;
    if (navigator.share) {
      try { await navigator.share({ title: "Credit Quack", text, url }); return; } catch {}
    }
    copyLink(code);
  };

  const sendIntake = async (u) => {
    if (!u.phone) { toast.error("No phone on file for this user."); return; }
    setBusy(true);
    try {
      await api.post(`/partner/referrals/${u.id}/send-intake-sms`);
      toast.success(`Intake link sent to ${u.name}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "SMS failed");
    } finally { setBusy(false); }
  };

  const totalSignups = codes.reduce((a, c) => a + (c.signups || 0), 0);
  const totalClicks = codes.reduce((a, c) => a + (c.clicks || 0), 0);
  const conv = totalClicks > 0 ? Math.round((totalSignups / totalClicks) * 100) : 0;

  return (
    <div className="mt-6 space-y-6" data-testid="referrals-tab">
      {/* Codes */}
      <div>
        <h2 className="font-display font-black text-2xl text-slate-900 flex items-center gap-2"><Rocket size={20} className="text-amber-500" /> Your referral codes</h2>
        <p className="text-sm text-slate-500 mt-1">Anyone who signs up with your code is auto-attributed to you. Share your link, we track everything.</p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          {codes.map((c) => {
            const url = buildUrl(c.code);
            const conv2 = c.clicks > 0 ? Math.round(((c.signups || 0) / c.clicks) * 100) : 0;
            return (
              <Card key={c.code} className="p-4 rounded-2xl border-slate-100" data-testid={`ref-code-${c.code}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-amber-500 text-white flex items-center justify-center font-black">{c.code[0]}</div>
                  <div className="flex-1">
                    <p className="font-display font-black text-slate-900 text-lg">{c.code}</p>
                    <p className="text-xs text-slate-500">{c.label} · {c.owner_name || "Unassigned"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Signups</p>
                    <p className="font-display font-black text-2xl text-slate-900">{c.signups || 0}</p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-slate-500">
                  <div><b className="text-slate-800">{c.clicks || 0}</b> clicks</div>
                  <div><b className="text-slate-800">{c.signups || 0}</b> signups</div>
                  <div><b className="text-slate-800">{conv2}%</b> conv</div>
                </div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <div className="flex-1 min-w-[160px] inline-flex items-center gap-2 px-2 h-9 rounded-md border border-slate-200 bg-slate-50 text-xs text-slate-600 truncate">
                    <Link2 size={12} className="text-slate-400" /> <span className="font-mono truncate">{url.replace(/^https?:\/\//, "")}</span>
                  </div>
                  <Button variant="outline" onClick={() => copyLink(c.code)} data-testid={`copy-${c.code}`}>
                    <Copy size={13} className="mr-1" /> Copy
                  </Button>
                  <Button onClick={() => share(c.code)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`share-${c.code}`}>
                    <Share2 size={13} className="mr-1" /> Share
                  </Button>
                </div>
              </Card>
            );
          })}
          {codes.length === 0 && (
            <Card className="p-6 rounded-2xl border-dashed border-slate-200 text-center text-sm text-slate-400">No codes yet — ask an admin to assign you one.</Card>
          )}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <MiniStat icon={<Users size={14} />} label="Total signups" value={totalSignups} />
          <MiniStat icon={<TrendingUp size={14} />} label="Total clicks" value={totalClicks} />
          <MiniStat icon={<Trophy size={14} />} label="Conversion" value={`${conv}%`} />
        </div>
      </div>

      {/* Referred users */}
      <div>
        <h2 className="font-display font-black text-2xl text-slate-900 flex items-center gap-2"><UserPlus size={20} className="text-emerald-500" /> Your referred clients</h2>
        <p className="text-sm text-slate-500 mt-1">Everyone who signed up with your code. Send them their intake link with one tap.</p>
        <Card className="mt-3 rounded-2xl border-slate-100 divide-y">
          {refs.length === 0 && <div className="p-8 text-center text-sm text-slate-400">Nobody yet — share your link to get started.</div>}
          {refs.map((u) => (
            <div key={u.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`ref-user-${u.id}`}>
              <div className="h-10 w-10 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold">{(u.name || "?")[0]}</div>
              <div className="flex-1 min-w-[180px]">
                <p className="font-display font-black text-slate-900">{u.name}</p>
                <p className="text-xs text-slate-500">
                  {u.email}
                  {u.phone ? ` · ${u.phone}` : ""}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Signed up {new Date(u.created_at).toLocaleDateString()} · code <b>{u.referred_by_code}</b>
                  {u.client_status ? ` · ${u.client_status}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-widest font-black rounded-full border px-2 py-0.5 ${u.intake_done ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                  {u.intake_done ? "Intake done" : "Intake pending"}
                </span>
                {!u.intake_done && (
                  <Button variant="outline" onClick={() => sendIntake(u)} disabled={busy || !u.phone} data-testid={`send-intake-${u.id}`}>
                    <Send size={13} className="mr-1" /> Send intake
                  </Button>
                )}
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

function MiniStat({ icon, label, value }) {
  return (
    <Card className="p-3 rounded-2xl border-slate-100 flex items-center gap-3">
      <div className="h-8 w-8 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center">{icon}</div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</p>
        <p className="font-display font-black text-lg text-slate-900">{value}</p>
      </div>
    </Card>
  );
}

// ============================================================
// Team Chat tab (Mark ↔ super_admins)
// ============================================================
function TeamChatTab() {
  const [threadsMode, setThreadsMode] = useState(false); // super_admin gets list first
  const [threads, setThreads] = useState([]);
  const [activePartner, setActivePartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (partnerId) => {
    try {
      const url = partnerId ? `/partner/team-chat?partner_id=${partnerId}` : `/partner/team-chat`;
      const { data } = await api.get(url);
      if (data.threads) {
        setThreadsMode(true);
        setThreads(data.threads);
        if (!activePartner && data.threads.length > 0) {
          setActivePartner(data.threads[0].partner);
          load(data.threads[0].partner.id);
        }
      } else {
        setMessages(data.items || []);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!threadsMode) {
      const t = setInterval(() => load(), 8000);
      return () => clearInterval(t);
    }
    if (activePartner) {
      const t = setInterval(() => load(activePartner.id), 8000);
      return () => clearInterval(t);
    }
  // eslint-disable-next-line
  }, [threadsMode, activePartner]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const body = { text: t };
      if (threadsMode && activePartner) body.partner_id = activePartner.id;
      const { data } = await api.post("/partner/team-chat", body);
      setMessages((prev) => [...prev, data]);
      setText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6" data-testid="team-chat-tab">
      <h2 className="font-display font-black text-2xl text-slate-900 flex items-center gap-2"><MessageSquare size={20} className="text-sky-500" /> Team chat</h2>
      <p className="text-sm text-slate-500 mt-1">Talk directly with the Credit Quack team — pricing, posting delays, escalations, wins.</p>
      <div className={`mt-4 grid grid-cols-1 ${threadsMode ? "lg:grid-cols-[280px,1fr]" : ""} gap-4`}>
        {threadsMode && (
          <Card className="rounded-2xl border-slate-100 divide-y" data-testid="team-partner-list">
            {threads.map((t) => (
              <button
                key={t.partner.id}
                onClick={() => { setActivePartner(t.partner); load(t.partner.id); }}
                className={`w-full text-left p-3 hover:bg-slate-50 ${activePartner?.id === t.partner.id ? "bg-amber-50" : ""}`}
                data-testid={`team-partner-${t.partner.id}`}
              >
                <p className="font-display font-black text-slate-900">{t.partner.name}</p>
                <p className="text-xs text-slate-500 truncate">{t.last_message?.text || "No messages yet"}</p>
              </button>
            ))}
            {threads.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No partners yet.</div>}
          </Card>
        )}
        <Card className="rounded-2xl border-slate-100 p-3 flex flex-col gap-2 min-h-[420px]" data-testid="team-chat-panel">
          <div className="flex-1 overflow-y-auto bg-slate-50 rounded-lg p-2 flex flex-col gap-1.5 min-h-[300px]">
            {messages.length === 0 && <p className="text-xs text-slate-400 text-center py-8">No messages yet — say something.</p>}
            {messages.map((m) => {
              const isMark = m.sender_role === "tradeline_admin";
              return (
                <div key={m.id} className={`flex ${isMark === !threadsMode ? "justify-end" : "justify-start"}`} data-testid={`tmsg-${m.id}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${isMark ? "bg-amber-500 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                    <p className={`text-[10px] font-bold mb-0.5 ${isMark ? "text-white/80" : "text-slate-500"}`}>{m.sender_name}</p>
                    <p className="whitespace-pre-wrap">{m.text}</p>
                    <p className={`text-[9px] mt-0.5 ${isMark ? "text-white/70" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Message the team…" data-testid="team-msg-input" />
            <Button onClick={send} disabled={busy || !text.trim()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="team-msg-send">
              <Send size={14} className="mr-1" /> Send
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

